import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
// Components
import { LCAlert } from '../../../components/alert';
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import ErrorMessage from '../../../components/error-message';
import { AutoFill } from '../../../components/field-autofill';
import EnhancedForm from '../../../components/form';
// Modules
import {
    actions as billActions,
    selectors as billSelectors,
    types as billTypes,
    ProductTypeExceptional,
} from '../../../redux/reducers/bills';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { SaveExceptionalPayload } from '../../../redux/reducers/api-types';
// Ressources
import FormattingUtils from '../../../utils/formattingUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { getListCompaniesMembershipURL, getListCompaniesURL, getListRidersURL } from '../../../utils/core-api';
import { FormDatas } from '../../../local-types';

interface ExceptionalAddProps extends WithTranslation, RouteComponentProps {
    saveExceptionalError: ErrorPayload;
    getBillsExceptionnalError: ErrorPayload;
    productTypesExceptional: ProductTypeExceptional[];
    saveExceptional: (payload: SaveExceptionalPayload, callback: React.EffectCallback) => any;
    getExceptionalProductTypes: () => void;
    isSavingExceptional: boolean;
}

interface ExceptionalAddPageState {
    formData: any;
}

class ExceptionalAddPage extends React.Component<ExceptionalAddProps, ExceptionalAddPageState> {
    inputs: FormDatas[];
    optionsMotif: any[];
    myRef: any;
    userRef: any;
    companyRef: any;
    myDialogRef: any;

    constructor(props: ExceptionalAddProps) {
        super(props);
        this.state = {
            formData: {},
        };

        this.optionsMotif = [];
    }

    componentDidMount() {
        this.props.getExceptionalProductTypes();
    }

    submitExceptional = (): void => {
        const { formData } = this.state;
        const { productTypesExceptional } = this.props;

        const product_type: ProductTypeExceptional = productTypesExceptional.find((p) => p.id === formData.motif);
        const vat_rate = product_type?.vat_rate ?? 0;

        const payload = {
            ...(formData.label && { label: formData.label }),
            customer: {
                ...(formData.rider?.uuid && { user_id: formData.rider.uuid }),
                ...(formData.company?.uuid && formData.is_pro && { company_id: formData.company.uuid }),
            },
            product_type: product_type?.name,
            amount_pre_tax: formData.price_ht,
            amount_with_tax: formData.price_ht + formData.price_ht * vat_rate,
        };

        this.props.saveExceptional(payload, () => {
            this.props.history.push(paths.billsExceptionals);
        });
    };

    handleReset = (): void => {
        this.userRef.reset();
        if (this.companyRef) {
            this.companyRef.reset();
        }
    };
    onSubmit = (formData: any, event: any): void => {
        this.setState({ formData });

        const isProfessionalWithMissingIds = formData.is_pro && !formData.rider?.id && !formData.company?.id;
        const isNonProfessionalWithMissingRider = !formData.is_pro && !formData.rider?.id;

        if (isProfessionalWithMissingIds || isNonProfessionalWithMissingRider) {
            this.myRef?.showAlert(); // Optional chaining to avoid runtime errors
        } else {
            this.myRef?.closeAlert();
            this.myDialogRef?.showDialog();
        }
    };
    computeAmount = (): number => {
        const { formData } = this.state;
        const { productTypesExceptional } = this.props;

        if (!formData || !productTypesExceptional) {
            return 0; // Default value if data is missing
        }

        const vatRate = productTypesExceptional.find((p) => p.id === formData.motif)?.vat_rate ?? 0;
        const amount = formData.price_ht + formData.price_ht * vatRate;

        return parseFloat(amount.toFixed(2)); // Ensure return type is a number
    };

    inputsBuilder = (): FormDatas[] => {
        const { t, productTypesExceptional } = this.props;
        this.inputs = [
            {
                title: t('add_exceptional'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        id: 'is_pro',
                        label: t('common:form.pro'),
                        type: 'boolean',
                        required: true,
                    },
                    {
                        id: 'company',
                        label: t('common:company'),
                        linked: { id: 'is_pro', value: true },
                        customView: (val, handleChange) => {
                            return (
                                <AutoFill
                                    formControl={false}
                                    onRef={(ref) => (this.companyRef = ref)}
                                    datas={{
                                        id: 'company',
                                        name: 'company',
                                        label: t('company'),
                                        minChar: 1,
                                        required: true,
                                        variant: 'outlined',
                                        style: { marginLeft: '16', paddingRight: 32 },
                                        onChange: (idc, text, item, list, event) => {
                                            handleChange(event, item);
                                        },
                                        loaderPosition: 'right',
                                        api: getListCompaniesURL(),
                                    }}
                                />
                            );
                        },
                    },
                    {
                        id: 'rider',
                        label: t('user'),
                        customView: (val, handleChange, formData) => {
                            const companyId = this.companyRef?.fieldInfos().selectedId;
                            let api = getListRidersURL();
                            let customDatalist = null;
                            let onFind = null;
                            if (companyId > -1) {
                                api = getListCompaniesMembershipURL(companyId);
                                customDatalist = (obj) => {
                                    const select = obj.rider;
                                    const value = `(${select.id}) ${select.fullname || select.name}`;
                                    return (
                                        <option key={select.id} value={value} data-id={select.id}>
                                            {value}
                                        </option>
                                    );
                                };
                                onFind = (datas, id) => {
                                    return datas.find((x) => x.rider && x.rider.id === id)?.rider;
                                };
                            }
                            return (
                                <AutoFill
                                    key={companyId}
                                    formControl={false}
                                    onRef={(ref) => (this.userRef = ref)}
                                    datas={{
                                        id: 'rider',
                                        name: 'rider',
                                        label: t('user'),
                                        minChar: 1,
                                        required: formData?.is_pro ? false : true,
                                        variant: 'outlined',
                                        style: { marginLeft: '16', paddingRight: 32 },
                                        onChange: (idc, text, item, list, event) => {
                                            handleChange(event, item);
                                        },
                                        onFind: onFind,
                                        loaderPosition: 'right',
                                        api: api,
                                        customDatalist: customDatalist,
                                    }}
                                />
                            );
                        },
                    },
                    {
                        id: 'motif',
                        label: t('motif'),
                        type: 'picker',
                        options: this.optionsMotif,
                        required: true,
                    },
                    {
                        id: 'price_ht',
                        label: `${t('common:price')} ${t('common:euro_ht')}`,
                        type: 'number',
                        required: true,
                    },
                    { id: 'label', label: t('common:form.additional_infos') },
                ],
                computeRules: [
                    {
                        target: 'price_ht',
                        by: 'price_ht',
                        computeHint: [
                            {
                                id: 'price_ht',
                                compute: (obj, val) => {
                                    const vat_rate =
                                        this.props.productTypesExceptional.find((p) => p.id === obj.motif)?.vat_rate ??
                                        0;
                                    return 'Total TTC: ' + FormattingUtils.price(+val + +val * vat_rate, true);
                                },
                            },
                        ],
                    },
                    {
                        target: 'price_ht',
                        by: 'motif',
                        computeHint: [
                            {
                                id: 'price_ht',
                                compute: (obj, _) => {
                                    const val = obj.price_ht;
                                    const vat_rate =
                                        this.props.productTypesExceptional.find((p) => p.id === obj.motif)?.vat_rate ??
                                        0;
                                    return 'Total TTC: ' + FormattingUtils.price(+val + +val * vat_rate, true);
                                },
                            },
                        ],
                    },
                ],
            },
        ];
        if (productTypesExceptional) {
            this.inputs[0].datas.find((d) => d.id === 'motif').options = productTypesExceptional.map((r) => {
                return {
                    id: r.id,
                    name: `${r.label} - TVA ${r.vat_rate * 100}%`,
                };
            });
        }
        return this.inputs;
    };

    render() {
        const { t, getBillsExceptionnalError, saveExceptionalError } = this.props;

        return (
            <App needRights={{ name: AccountRight.EXCEPTIONAL_BILLING_MGMT, mode: RightRank.W }}>
                <ErrorMessage payload={getBillsExceptionnalError || saveExceptionalError} />
                <LCAlert
                    onRef={(ref) => (this.myRef = ref)}
                    details={t('exceptional.error')}
                    type="error"
                    open={false}
                />
                <Dialog
                    onRef={(ref) => (this.myDialogRef = ref)}
                    btnTitle={t('exceptional.pop_up_title')}
                    description={t('exceptional.pop_up_msg', {
                        amount: this.computeAmount(),
                    })}
                    customButton={<></>}
                    onOK={this.submitExceptional}
                />
                <EnhancedForm
                    payload={this.state.formData}
                    inputs={this.inputsBuilder()}
                    backRoute={paths.billsExceptionals}
                    onReset={this.handleReset}
                    onSubmit={this.onSubmit}
                    loader={this.props.isSavingExceptional}
                />
            </App>
        );
    }
}

const ExceptionalAddPageWithStyleAndTranslation = withTranslation(['bill', 'common'])(ExceptionalAddPage);

const mapStateToProps = (state) => ({
    productTypesExceptional: billSelectors.getProductTypesExceptional(state),
    getBillsExceptionnalError: errorSelectors.getBillsExceptionnalError(state),
    saveExceptionalError: errorSelectors.saveExceptionalError(state),
    isSavingExceptional: pendingSelectors.isPending(state, billTypes.CREATE_BILLS_EXCEPTIONAL),
});

const mapDispatchToProps = {
    saveExceptional: billActions.saveExceptional,
    getExceptionalProductTypes: billActions.getExceptionalProductTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(ExceptionalAddPageWithStyleAndTranslation);
